import {
  Badge,
  Box,
  Button,
  HStack,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  Input,
} from "@chakra-ui/react"
import axios from "axios"
import { months } from "components/Group/GroupClassBox"
// @ts-ignore
import dateFormat from "dateformat"
import * as React from "react"
import { useHistory } from "react-router-dom"
import { Group } from "types/apiTypes"
import createPersistedState from "use-persisted-state"
import { getErrors } from "utils/functions"
import { api } from "../../config"
import { useQuery } from "@tanstack/react-query"
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons"

const ViewPayments: React.FC = () => {
  const usePaymentState = createPersistedState("sortQueryPayments")
  const [sortQueryPayment, setSortQueryPayment]: any =
    usePaymentState("paidDate:desc")
  const history = useHistory()
  const toast = useToast()
  const [groups, setGroups] = React.useState<Group[]>([])
  const [filterGroup, setFilterGroup] = React.useState<string>("")
  const [filterMonth, setFilterMonth] = React.useState<string>("")
  const [filterStudent, setFilterStudent] = React.useState<string>("")
  const [filterType, setFilterType] = React.useState("")
  const [filterAmount, setFilterAmount] = React.useState("")
  const [filterDateFrom, setFilterDateFrom] = React.useState("")
  const [filterDateTo, setFilterDateTo] = React.useState("")

  const [page, setPage] = React.useState(1)
  const limit = 10
  const start = (page - 1) * limit

  const fetchPayments = async () => {
    try {
      const { data: payments } = await axios.get(
        `${api}/payments?id_ne=0&_sort=${sortQueryPayment}` +
          `&_limit=${limit}&_start=${start}` +
          `${filterGroup ? `&month.group.id=${filterGroup}` : ""}` +
          `${filterMonth ? `&month.month=${filterMonth}` : ""}` +
          `${
            filterStudent
              ? `&users_permissions_user.username=${filterStudent}`
              : ""
          }` +
          `${filterType ? `&type=${filterType}` : ""}` +
          `${filterAmount ? `&paidAmount=${filterAmount}` : ""}` +
          `${filterDateFrom ? `&paidDate_gte=${filterDateFrom}` : ""}` +
          `${filterDateTo ? `&paidDate_lte=${filterDateTo}` : ""}`,
        { withCredentials: true },
      )

      return payments
    } catch (error: any) {
      toast({
        title: error.message,
        position: "bottom-right",
        status: "error",
      })

      return []
    }
  }

  const fetchGroups = async () => {
    try {
      const { data: groupsData } = await axios.get(`${api}/groups`, {
        withCredentials: true,
      })
      setGroups(groupsData)
    } catch (error: any) {
      toast({
        title: error.message,
        position: "bottom-right",
        status: "error",
      })
    }
  }

  const handleSelectGroup = (e: any) => setFilterGroup(e.target.value)
  const handleSelectMonth = (e: any) => setFilterMonth(e.target.value)
  const handleSelectStudent = (e: any) => setFilterStudent(e.target.value)

  React.useEffect(() => {
    fetchGroups()
  }, [])

  const { data: payments = [], isLoading, refetch } = useQuery({
    queryKey: [
      "payments",
      filterGroup,
      filterMonth,
      filterStudent,
      filterType,
      filterAmount,
      filterDateFrom,
      filterDateTo,
      sortQueryPayment,
      page,
    ],
    queryFn: fetchPayments,
  })

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure?")) {
      try {
        await axios.delete(`${api}/payments/${id}`, { withCredentials: true })
        toast({
          title: "Payment Deleted Successfully",
          position: "bottom-right",
          status: "success",
        })
        refetch()
      } catch (err: any) {
        const errors = getErrors(err)
        errors.forEach((e) =>
          toast({ title: e, position: "bottom-right", status: "error" }),
        )
      }
    }
  }

  const handleExport = async () => {
    try {
      const query =
        `${api}/payments/export?_sort=${sortQueryPayment}` +
        `${filterGroup ? `&month.group.id=${filterGroup}` : ""}` +
        `${filterMonth ? `&month.month=${filterMonth}` : ""}` +
        `${filterStudent ? `&users_permissions_user.username=${filterStudent}` : ""}` +
        `${filterType ? `&type=${filterType}` : ""}` +
        `${filterAmount ? `&paidAmount=${filterAmount}` : ""}` +
        `${filterDateFrom ? `&paidDate_gte=${filterDateFrom}` : ""}` +
        `${filterDateTo ? `&paidDate_lte=${filterDateTo}` : ""}`

      const res = await axios.get(query, {
        responseType: "blob",
        withCredentials: true,
      })

      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement("a")

      link.href = url
      link.setAttribute("download", "payments.xlsx")
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      toast({
        title: "Export failed",
        position: "bottom-right",
        status: "error",
      })
    }
  }

  const handlePrev = () => setPage((prev) => Math.max(prev - 1, 1))
  const handleNext = () => {
    if (payments.length === limit) setPage((prev) => prev + 1)
  }

  return (
    <>
      <Box
        gridTemplateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        display="grid"
        gridGap={{ base: 3, md: 6 }}
        my={{ base: 2, md: 6 }}
        bg="blue.100"
        p="2"
        shadow="base"
        py="3"
      >
        {/* Filter UI */}
        <Box>
          <Text>Filter by a class</Text>
          <Select
            placeholder="Select a class"
            onChange={handleSelectGroup}
            bg="white"
          >
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.groupName}
              </option>
            ))}
          </Select>
        </Box>
        <Box>
          <Text>Filter by a month</Text>
          <Select
            placeholder="Select a month"
            onChange={handleSelectMonth}
            bg="white"
          >
            {months.map((month, index) => (
              <option key={index} value={index + 1}>
                {month}
              </option>
            ))}
          </Select>
        </Box>
        <Box>
          <Text>Filter by a Student</Text>
          <Select
            placeholder="Select a class first"
            onChange={handleSelectStudent}
            bg="white"
          >
            {filterGroup !== "" &&
              groups
                .find((x) => x.id.toString() === filterGroup)
                ?.users_permissions_users.map((user, index) => (
                  <option key={index} value={user.username}>
                    {user.username}
                  </option>
                ))}
          </Select>
        </Box>
      </Box>
      <Box
        gridTemplateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
        display="grid"
        gridGap={{ base: 3, md: 6 }}
        my={{ base: 2, md: 6 }}
        bg="blue.100"
        p="2"
        shadow="base"
        py="3"
      >
        <Box>
          <Text>Filter by Type</Text>
          <Select
            placeholder="Select type"
            onChange={(e) => {
              setFilterType(e.target.value)
              setPage(1)
            }}
            bg="white"
          >
            <option value="BS">Receipt Uploaded</option>
            <option value="PH">Payhere</option>
            <option value="ST">Stripe</option>
          </Select>
        </Box>

        <Box>
          <Text>Filter by Amount</Text>
          <Input
            placeholder="Enter amount"
            bg="white"
            onChange={(e) => {
              setFilterAmount(e.target.value)
              setPage(1)
            }}
          />
        </Box>

        <Box>
          <Text>Paid Date From</Text>
          <Input
            type="date"
            bg="white"
            onChange={(e) => {
              setFilterDateFrom(e.target.value)
              setPage(1)
            }}
          />
        </Box>

        <Box>
          <Text>Paid Date To</Text>
          <Input
            type="date"
            bg="white"
            onChange={(e) => {
              setFilterDateTo(e.target.value)
              setPage(1)
            }}
          />
        </Box>
        <Box display="flex" alignItems="end">
          <Button colorScheme="blue" width="100%" onClick={handleExport}>
            Export Excel
          </Button>
        </Box>
      </Box>

      <Box mt="4">
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <Box
              overflowX="auto"
              width="100%"
              sx={{
                "& table": {
                  tableLayout: "auto",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <Table variant="simple" minW="1800px">
                <Thead>
                  <Tr>
                    <Th>Group</Th>
                    <Th>Codice</Th>
                    <Th>Month</Th>
                    <Th>Paid Date</Th>
                    <Th>Paid Amount</Th>
                    <Th>Payment Type</Th>
                    <Th>Receipt</Th>
                    <Th>Actions</Th>
                    <Th>Payment Person</Th>
                    <Th>CF</Th>
                    <Th>City</Th>
                    <Th>Address</Th>
                    <Th>CAP</Th>
                    <Th>Code</Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {payments.map((item: any) => {
                    const persons = Array.isArray(item.payment_person)
                      ? item.payment_person
                      : item.payment_person
                        ? [item.payment_person]
                        : []

                    return (
                      <Tr key={item.id}>
                        <Td>{item.month?.group?.groupName || "-"}</Td>

                        <Td>{item.users_permissions_user?.username || "-"}</Td>

                        <Td>
                          {item.month
                            ? `${months[item.month.month - 1]} ${item.month.year}`
                            : "-"}
                        </Td>

                        <Td>
                          {item.paidDate
                            ? dateFormat(
                                item.paidDate,
                                "yyyy-mm-dd, h:MM:ss TT",
                              )
                            : "-"}
                        </Td>

                        <Td>{item.paidAmount ?? "-"}</Td>

                        <Td>
                          {item.type === "BS" ? (
                            <Badge bg="blue.100">Receipt Uploaded</Badge>
                          ) : item.type === "PH" ? (
                            <Badge bg="green.100">Payhere Payment</Badge>
                          ) : item.type === "ST" ? (
                            <Badge bg="yellow.100">Stripe Card Payment</Badge>
                          ) : (
                            "-"
                          )}
                        </Td>

                        <Td>
                          {item.slip ? (
                            <a
                              href={`${api}${item.slip}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={`${api}${item.slip}`}
                                style={{ width: 50 }}
                              />
                            </a>
                          ) : (
                            <>-</>
                          )}
                        </Td>

                        <Td>
                          <button
                            onClick={() => handleDelete(item.id)}
                            style={{ color: "red", fontSize: 12 }}
                          >
                            Delete
                          </button>
                        </Td>

                        <Td>
                          {persons.length > 0
                            ? persons
                                .map((p: any) =>
                                  `${p?.personName || ""} ${p?.surname || ""}`.trim(),
                                )
                                .join(", ")
                            : "-"}
                        </Td>

                        <Td>
                          {persons.length > 0
                            ? persons.map((p: any) => p?.cf || "-").join(", ")
                            : "-"}
                        </Td>

                        <Td>
                          {persons.length > 0
                            ? persons.map((p: any) => p?.city || "-").join(", ")
                            : "-"}
                        </Td>

                        <Td>
                          {persons.length > 0
                            ? persons
                                .map((p: any) => p?.address || "-")
                                .join(", ")
                            : "-"}
                        </Td>

                        <Td>
                          {persons.length > 0
                            ? persons.map((p: any) => p?.cap || "-").join(", ")
                            : "-"}
                        </Td>

                        <Td>
                          {persons.length > 0
                            ? persons.map((p: any) => p?.code || "-").join(", ")
                            : "-"}
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </Box>

            <HStack justify="center" mt="4">
              <Button
                onClick={handlePrev}
                isDisabled={page === 1}
                leftIcon={<ArrowBackIcon />}
              />

              <Text>Page {page}</Text>

              <Button
                onClick={handleNext}
                isDisabled={payments.length < limit}
                rightIcon={<ArrowForwardIcon />}
              />
            </HStack>
          </>
        )}
      </Box>
    </>
  )
}

export default ViewPayments;
